import { useMemo } from "react";

import { MapState } from "@/sx-layout/hooks/useMapState";

type BoundingBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type ElementSize = {
  width: number;
  height: number;
};

export const useFloatingBottomPosition = (
  boundingBox: BoundingBox,
  mapState: MapState,
  elementSize: ElementSize,
  margin: number
) => {
  const { mapRatio, viewport } = mapState;

  return useMemo(() => {
    const viewportMargin = 10;
    const viewportLeft = (viewport?.left ?? 0) * mapRatio;
    const viewportRight = ((viewport?.left ?? 0) + (viewport?.width ?? 0)) * mapRatio;
    const viewportTop = (viewport?.top ?? 0) * mapRatio;
    const viewportBottom = ((viewport?.top ?? 0) + (viewport?.height ?? 0)) * mapRatio;

    // 1. 水平位置 (left) の計算
    const calculateLeft = () => {
      const defaultCenter = (boundingBox.x + boundingBox.width / 2) * mapRatio - elementSize.width / 2;

      // オブジェクトの中央が表示領域外（左）の場合は表示領域の左に表示
      if (defaultCenter < viewportLeft) {
        return { left: viewportLeft + viewportMargin, originX: "left" as const };
      }
      // オブジェクトの中央が表示領域外（右）の場合は表示領域の右に表示
      if (viewportRight < defaultCenter + elementSize.width) {
        return { left: viewportRight - elementSize.width - viewportMargin, originX: "right" as const };
      }
      // オブジェクトの中央に表示（デフォルト）
      return { left: defaultCenter, originX: "center" as const };
    };

    // 2. 垂直位置 (top) の計算
    const calculateTop = () => {
      const spaceBelow = viewportBottom - (boundingBox.y + boundingBox.height) * mapRatio;
      const spaceAbove = boundingBox.y * mapRatio - viewportTop;

      const topBelow = (boundingBox.y + boundingBox.height) * mapRatio + margin;
      const topAbove = boundingBox.y * mapRatio - margin - elementSize.height;

      // 下に表示するスペースがない場合は上に表示
      if (spaceBelow < elementSize.height + margin && spaceAbove > elementSize.height + margin) {
        return { top: topAbove, originY: "bottom" as const };
      }

      // 上下に表示するスペースがない場合は表示領域の下の方に表示する（オブジェクトが大きい・拡大率が大きい場合）
      if (spaceBelow < elementSize.height + margin && spaceAbove < elementSize.height + margin) {
        return { top: viewportBottom - elementSize.height - viewportMargin, originY: "bottom" as const };
      }

      // オブジェクトの下側に表示（デフォルト）
      return { top: topBelow, originY: "top" as const };
    };

    const { left, originX } = calculateLeft();
    const { top, originY } = calculateTop();

    return {
      top,
      left,
      transformOrigin: `${originY} ${originX}`,
    };
  }, [boundingBox, viewport, elementSize, margin, mapRatio]);
};
